import { memo } from 'react';
import { useTranslation } from 'next-i18next';
import { showImageUploadModal } from '@/shared/features/image-upload/components';
import { cn } from '@/utils';
import { useImageUploadInput } from '@/views/build/hook/useImageUploadInput';
import { useFocusTarget } from '../../focus/useFocusTarget';
import { EditTools } from '../EditTools';
import { HeightAnimate } from '../HeightAnimate';
import { useDroppableImageUploadInput } from '../hook/useDroppableImageUploadInput';
import { usePasteFile } from '../hook/usePasteFile';
import { useQuizFormField } from '../hook/useQuizFormField';
import { useQuizControl } from '../QuizControlProvider';
import { TextEditor } from '../TextEditor';
import { EditableQuestionImage } from './EditableQuestionImage';
import { QuestionEditTools } from './QuestionEditTools';
import { useQuestionFieldTextEditor } from './useQuestionFieldTextEditor';
export const EditableQuestion = memo(({
  additionalTools
}: {
  additionalTools?: React.ReactNode[];
}) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    index
  } = useQuizControl();
  const questionRef = useFocusTarget<HTMLDivElement>(`quizList.${index}.question`);
  const {
    editor
  } = useQuestionFieldTextEditor({
    quizIndex: index
  });
  const [questionImageUrl, updateQuestionImageUrl] = useQuizFormField({
    field: 'questionImageUrl'
  });
  const {
    handleFileChange,
    accept,
    isUploading
  } = useImageUploadInput({
    onUploadComplete: imageUrl => {
      updateQuestionImageUrl(imageUrl);
    }
  });
  const handleFileChangeVoid = (file: File | null) => {
    void handleFileChange(file);
  };
  const {
    rootProps,
    inputProps,
    isDragActive,
    inputRef
  } = useDroppableImageUploadInput({
    onFileChange: handleFileChangeVoid,
    accept,
    imageUrl: questionImageUrl
  });
  usePasteFile({
    editor,
    shouldConfirm: !!questionImageUrl,
    onPaste: handleFileChangeVoid
  });
  return <div className="relative flex flex-col gap-xs">
        <HeightAnimate>
          <div className="group" {...rootProps}>
            <input {...inputProps} className="hidden" ref={inputRef} />
            <TextEditor.Input ref={questionRef} placeholder={t('createSubjective.question.placeholder')} placeholderClassName="text-heading-md-bold" editor={editor} className={cn('text-heading-md-bold', isDragActive && 'shadow-[inset_0_-2px_0_0_theme(colors.violet.300)]')} />

            <EditTools.InlineTooltipContainer className={'hidden group-focus-within:flex'}>
              <QuestionEditTools editor={editor} additionalTools={[<EditTools.ImageAddButton onClick={() => {
            showImageUploadModal({
              onUploadStart: handleFileChangeVoid,
              place: 'build',
              imageUrl: questionImageUrl
            });
          }} />, ...(additionalTools ?? [])]} />
            </EditTools.InlineTooltipContainer>
          </div>
        </HeightAnimate>
        <EditableQuestionImage isUploading={isUploading} imageUrl={questionImageUrl} onRemove={() => {
      updateQuestionImageUrl(undefined);
    }} />
      </div>;
});